import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Building2, Loader2, FileText, CalendarDays, CreditCard, TrendingUp } from "lucide-react";
import { toast } from "sonner";

export default function Venue360() {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);

  useEffect(() => {
    api.get(`/admin/venues/${venueId}/full`)
      .then((res) => setData(res.data))
      .catch(() => toast.error("Failed to load Venue 360"))
      .finally(() => setLoading(false));
  }, [venueId]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;
  if (!data) return null;

  const paymentRows = [
    ...(data.payments?.payment_transactions || []).map((item) => ({ ...item, transaction_type: "stripe_or_card" })),
    ...(data.payments?.eplayment_transactions || []).map((item) => ({ ...item, transaction_type: "eplayment" })),
    ...(data.payments?.wallet_transactions || []).map((item) => ({ ...item, transaction_type: "wallet" })),
    ...(data.disbursements || []).map((item) => ({ ...item, transaction_type: "disbursement" })),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10 space-y-6" data-testid="venue-360-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">Venue 360</h1>
          <p className="text-sm text-muted-foreground">{data.venue.name}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Metric title="Bookings" value={data.analytics.total_bookings} icon={CalendarDays} />
        <Metric title="Payments" value={`₱${Number(data.analytics.total_payments || 0).toLocaleString()}`} icon={CreditCard} />
        <Metric title="Disbursement" value={`₱${Number(data.analytics.total_disbursement || 0).toLocaleString()}`} icon={TrendingUp} />
        <Metric title="Commission" value={`₱${Number(data.analytics.total_commission_earnings || 0).toLocaleString()}`} icon={Building2} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="font-heading text-lg">Registration & Documents</CardTitle></CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <p><span className="text-muted-foreground">Venue:</span> {data.venue.name}</p>
              <p><span className="text-muted-foreground">Owner:</span> {data.owner?.name || '—'}</p>
              <p><span className="text-muted-foreground">Business Type:</span> {data.venue.business_type || '—'}</p>
              <p><span className="text-muted-foreground">Registration #:</span> {data.venue.business_registration_number || '—'}</p>
              <p><span className="text-muted-foreground">Signatory:</span> {data.venue.authorized_signatory_name || '—'}</p>
              <p><span className="text-muted-foreground">Verified:</span> {data.venue.verified_at?.slice(0, 10) || '—'}</p>
            </div>
            <div className="space-y-2">
              {(data.documents || []).map((doc) => (
                <div key={doc.document_id} className="rounded-lg border border-slate-200 p-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium">{doc.label || doc.doc_type}</p>
                    <p className="text-xs text-muted-foreground">{doc.file_name || 'Uploaded document'}</p>
                  </div>
                  {doc.file_url && <a href={doc.file_url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">View</a>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-heading text-lg">Assets & Facilities</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {data.resources?.map((resource) => (
              <div key={resource.resource_id} className="rounded-lg border border-slate-200 p-3" data-testid={`venue-360-resource-${resource.resource_id}`}>
                <p className="font-medium">{resource.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{resource.sport_type} · {resource.attributes?.surface || 'Standard'}</p>
              </div>
            ))}
            {data.spaces?.map((space) => (
              <div key={space.space_id} className="rounded-lg border border-slate-200 p-3" data-testid={`venue-360-space-${space.space_id}`}>
                <p className="font-medium">{space.name}</p>
                <p className="text-xs text-muted-foreground">{space.space_type} · {space.surface_type || 'standard'} · {space.is_indoor ? 'Indoor' : 'Outdoor'}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-heading text-lg">Activity — Bookings</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(data.bookings || []).map((booking) => (
            <div key={`${booking.booking_source}-${booking.booking_id}`} className="rounded-lg border border-slate-200 p-3 flex items-center justify-between gap-3" data-testid={`venue-360-booking-${booking.booking_id}`}>
              <div>
                <p className="font-medium">{booking.user_name || booking.user_email || booking.booking_id}</p>
                <p className="text-xs text-muted-foreground">{booking.booking_source} · {booking.resource_name || booking.activity_name || booking.space_name || booking.booking_id}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{booking.status}</Badge>
                <Button size="sm" variant="outline" onClick={() => setSelectedBooking(booking)} data-testid={`view-booking-360-${booking.booking_id}`}>View Details</Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-heading text-lg">Transactions</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {paymentRows.map((payment, idx) => (
            <div key={`${payment.transaction_type}-${payment.reference_id || payment.transaction_id || idx}`} className="rounded-lg border border-slate-200 p-3 flex items-center justify-between gap-3" data-testid={`venue-360-payment-${idx}`}>
              <div>
                <p className="font-medium">{payment.transaction_type}</p>
                <p className="text-xs text-muted-foreground">{payment.booking_id || payment.reference_id || payment.transaction_id || payment.batch_id || 'Reference unavailable'}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{payment.status || payment.payment_status || 'posted'}</Badge>
                <Button size="sm" variant="outline" onClick={() => setSelectedPayment(payment)} data-testid={`view-payment-360-${idx}`}>View Details</Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="venue-360-booking-dialog">
          <DialogHeader><DialogTitle>Booking Details</DialogTitle></DialogHeader>
          {selectedBooking && <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(selectedBooking, null, 2)}</pre>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedPayment} onOpenChange={() => setSelectedPayment(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="venue-360-payment-dialog">
          <DialogHeader><DialogTitle>Payment / Disbursement Details</DialogTitle></DialogHeader>
          {selectedPayment && <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(selectedPayment, null, 2)}</pre>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ title, value, icon: Icon }) {
  return (
    <Card>
      <CardContent className="p-4">
        <Icon className="w-5 h-5 text-court-orange mb-2" />
        <p className="font-heading text-2xl font-bold">{value}</p>
        <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{title}</p>
      </CardContent>
    </Card>
  );
}